import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Eye } from "lucide-react";
import { Button } from "../../components/ui/button/button.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { ApprovalCenterStats } from "../../components/features/approve-bank-details/components/approval-center-stats.tsx";
import { ApprovalCenterFilters } from "../../components/features/approve-bank-details/components/approval-center-filters.tsx";
import { EmployeeTableAvatar } from "../../components/features/approve-bank-details/components/employee-table-avatar.tsx";
import { BankDetailsReadOnlyModal } from "../../components/features/approve-bank-details/modals/bank-details-readonly-modal.tsx";
import { EmployeeVerificationModal } from "../../components/features/approve-bank-details/modals/employee-verification-modal.tsx";
import { InvitePostJoinModal } from "../../components/features/onboard-form/modals/invite-post-join-modal.tsx";
import {
  DEFAULT_APPROVAL_CENTER_STATUS,
  SQE_APPROVAL_CENTER_STATUS_OPTIONS,
  SQE_DEFAULT_APPROVAL_CENTER_STATUS,
} from "../../components/features/approve-bank-details/constants.ts";
import {
  usePhase2List,
  type Phase2ListFilters,
} from "../../components/features/approve-bank-details/hooks/use-phase2-list.ts";
import type { Phase2ListRow } from "../../components/features/approve-bank-details/schemas/phase2-list.schema.ts";
import { useEmployeesList } from "../../hooks/time-allocated-overview/use-employees-list.ts";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";
import { useSqeEmployeeNameOptions } from "../../hooks/use-sqe-employee-name-options.ts";
import { employeeTypeLabel } from "../../utils/employee-type.ts";
import {
  Badge,
  ResponsiveDataTable,
  Spinner,
  type BadgeVariant,
  type ResponsiveColumn,
} from "../../components/ui/index.ts";
import { ApiError } from "../../utils/api.ts";
import { useUser } from "../../lib/rbac/useUser.ts";
import type { UserRole } from "../../lib/rbac/types.ts";
import { showError } from "../../utils/toast.ts";
import { toast } from "sonner";
import approvalStatusOptions from "../../../options/approvalStatusOptions.js";
import type { PageProps } from "../../types/page.ts";

import styles from "./approve-bank-details-page.module.css";

const HR_APPROVER_ROLES: UserRole[] = ["HR", "SUPERADMIN"];
const PENDING_PRE_JOINING_STATUS = "PENDING_PREJOINING";

/** Finance read-only when user has Finance and not HR/Superadmin. */
function isFinanceOnlyView(roles: UserRole[] | undefined): boolean {
  if (roles == null || roles.length === 0) return false;
  const hasFinance = roles.includes("Expense_Mgmt_Finance");
  const hasHrApprover = roles.some((role) => HR_APPROVER_ROLES.includes(role));
  return hasFinance && !hasHrApprover;
}

function buildInitialFilters(financeOnlyView: boolean, isSqe: boolean): Phase2ListFilters {
  return {
    status: financeOnlyView ? "APPROVED" : isSqe ? SQE_DEFAULT_APPROVAL_CENTER_STATUS : DEFAULT_APPROVAL_CENTER_STATUS,
    employeeId: undefined,
    region: undefined,
    fromDate: null,
    toDate: null,
  };
}

type ApprovalCenterTableRow = Phase2ListRow & { id: string } & Record<string, unknown>;

function formatDoj(iso: string | undefined): string {
  if (iso == null || iso === "") return "—";

  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;

  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
}

function getStatusBadge(status: string | undefined) {
  const normalized = (status ?? "").toUpperCase();
  const workflowLabels: Record<string, string> = {
    PENDING_POSTJOINING: "Pending Post-Joining",
    PENDING_REVIEW: "Pending Review",
    PENDING_INSURANCE: "Pending Insurance",
    NEEDS_CORRECTION: "Needs Correction",
  };
  if (workflowLabels[normalized]) {
    return <Badge variant={normalized === "NEEDS_CORRECTION" ? "danger" : "warning"}>{workflowLabels[normalized]}</Badge>;
  }
  const option = approvalStatusOptions.find((item) => item.value === normalized);
  if (option != null) {
    return (
      <Badge variant={option.type as BadgeVariant}>{option.label}</Badge>
    );
  }
  if (normalized === "SUBMITTED") {
    return <Badge variant="warning">Pending</Badge>;
  }
  return <Badge variant="neutral">{status || "—"}</Badge>;
}

export default function ApproveBankDetailsPage({ title }: PageProps) {
  const isSqe = useSqeLabs();
  const { setBreadcrumbs } = useBreadcrumbs();
  const user = useUser();
  const isFinanceOnly = isFinanceOnlyView(user?.roles);
  const effectiveInitialFilters = useMemo(
    () => buildInitialFilters(isFinanceOnly, isSqe),
    [isFinanceOnly, isSqe],
  );
  const [filters, setFilters] = useState<Phase2ListFilters>(
    effectiveInitialFilters,
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Phase2ListRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmployeeId, setInviteEmployeeId] = useState<number | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const regularEmployees = useEmployeesList(!isSqe);
  const sqeEmployees = useSqeEmployeeNameOptions(isSqe);
  const { options: employeeOptions, loading: employeesLoading, error: employeesError } = isSqe ? sqeEmployees : regularEmployees;

  const {
    rows,
    totalElements,
    stats,
    loading,
    statsLoading,
    error,
    refreshAll,
    approve,
    reject,
    approveDocument,
    rejectDocument,
    fetchChequeBlob,
    fetchFileBlob,
  } = usePhase2List(filters, page, pageSize);

  useEffect(() => {
    setBreadcrumbs([{ label: title ?? "Employee Insights" }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, title]);

  useEffect(() => {
    if (isSqe) {
      if (error) showError(error, { id: "approval-center-list-error" });
      else toast.dismiss("approval-center-list-error");
    } else if (error) {
      showError(error);
    }
  }, [error, isSqe]);

  useEffect(() => {
    if (employeesError) showError(employeesError);
  }, [employeesError]);

  useEffect(() => {
    if (!modalOpen || selectedRow == null) return;
    const fresh = rows.find(
      (r) =>
        r.employeeId === selectedRow.employeeId &&
        r.phase2Id === selectedRow.phase2Id,
    );
    if (fresh != null && fresh !== selectedRow) setSelectedRow(fresh);
  }, [rows, modalOpen, selectedRow?.employeeId, selectedRow?.phase2Id]);

  const tableRows: ApprovalCenterTableRow[] = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: `${row.employeeId}-${row.phase2Id}`,
      })),
    [rows],
  );

  const openRow = useCallback((row: Phase2ListRow) => {
    setSelectedRow(row);
    setModalOpen(true);
  }, []);

  const openInviteModal = useCallback((row: Phase2ListRow) => {
    setInviteEmployeeId(row.employeeId);
    setInviteOpen(true);
  }, []);

  const closeInviteModal = useCallback(() => {
    setInviteOpen(false);
    setInviteEmployeeId(null);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleApplyFilters = useCallback(
    (next: Phase2ListFilters) => {
      setFilters(isFinanceOnly ? { ...next, status: "APPROVED" } : next);
      setPage(1);
    },
    [isFinanceOnly],
  );

  const handleResetFilters = useCallback(() => {
    setFilters(effectiveInitialFilters);
    setPage(1);
  }, [effectiveInitialFilters]);

  const handleSelectStats = useCallback((status: string | undefined) => {
    if (isFinanceOnly && status !== "APPROVED") return;
    setFilters({
      ...effectiveInitialFilters,
      status,
    });
    setPage(1);
  }, [effectiveInitialFilters, isFinanceOnly]);

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const handleApproveBank = useCallback(async (employeeId: number) => {
    setActionBusy(true);
    try {
      await approve(employeeId);
      await refreshAll();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Approve failed.";
      throw new Error(msg);
    } finally {
      setActionBusy(false);
    }
  }, [approve, refreshAll]);

  const handleRejectBank = useCallback(
    async (employeeId: number, comment: string) => {
      setActionBusy(true);
      try {
        await reject(employeeId, comment);
        await refreshAll();
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Reject failed.";
        throw new Error(msg);
      } finally {
        setActionBusy(false);
      }
    },
    [reject, refreshAll],
  );

  const handleApproveDocument = useCallback(
    async (documentId: number) => {
      try {
        await approveDocument(documentId);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Document approve failed.";
        throw new Error(msg);
      }
    },
    [approveDocument],
  );

  const handleRejectDocument = useCallback(
    async (documentId: number, reason: string) => {
      try {
        await rejectDocument(documentId, reason);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Document reject failed.";
        throw new Error(msg);
      }
    },
    [rejectDocument],
  );

  const handleBatchComplete = useCallback(async () => {
    await refreshAll();
  }, [refreshAll]);

  const columns = useMemo<ResponsiveColumn<ApprovalCenterTableRow>[]>(
    () => [
      {
        id: "employee",
        header: "Employee Name",
        sortable: true,
        truncate: false,
        render: (row) => (
          <div className={styles.employeeCell}>
            <EmployeeTableAvatar
              employeeId={row.employeeId}
              name={row.employeeName}
              photoPath={row.photoPath}
              size="md"
            />
            <div className={styles.employeeMeta}>
              <span className={clsx("body-sm", styles.employeeName)}>
                {row.employeeName}
              </span>
              <span className={clsx("body-xs", styles.employeeEmail)}>
                {row.designation?.trim() ? row.designation : "—"}
              </span>
            </div>
          </div>
        ),
        getSortValue: (row) => row.employeeName,
        getTitleText: (row) => {
          const designation = row.designation?.trim();
          return designation
            ? `${row.employeeName} — ${designation}`
            : row.employeeName;
        },
      },
      {
        id: "employeeCode",
        header: "Employee Code",
        accessor: "employeeCode",
        sortable: true,
        nowrap: true,
        truncate: true,
      },
      {
        id: "email",
        header: "Email",
        accessor: "email",
        sortable: true,
        // truncate: true,
        getTitleText: (row) => row.email,
      },
      {
        id: "phase2CreatedOn",
        header: "Date of Joining",
        sortable: true,
        render: (row) => (
          <span className="body-sm">{formatDoj(row.doj)}</span>
        ),
        getSortValue: (row) =>
          row.doj
            ? new Date(row.doj).getTime()
            : 0,
        getTitleText: (row) => formatDoj(row.doj),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        render: (row) => getStatusBadge(row.workflowStatus),
        getSortValue: (row) => row.workflowStatus,
      },
      {
        id: "actions",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <button
            type="button"
            className={styles.viewBtn}
            aria-label={`View request for ${row.employeeName}`}
            onClick={() => openRow(row)}
          >
            <Eye size={16} strokeWidth={2} aria-hidden />
            View
          </button>
        ),
      },
    ],
    [openRow],
  );

  const pendingPreJoiningColumns = useMemo<
    ResponsiveColumn<ApprovalCenterTableRow>[]
  >(
    () => [
      {
        id: "employee",
        header: "Employee Name",
        sortable: true,
        truncate: false,
        render: (row) => (
          <div className={styles.employeeCell}>
            <EmployeeTableAvatar
              employeeId={row.employeeId}
              name={row.employeeName}
              photoPath={row.photoPath}
              size="md"
            />
            <div className={styles.employeeMeta}>
              <span className={clsx("body-sm", styles.employeeName)}>
                {row.employeeName}
              </span>
              <span className={clsx("body-xs", styles.employeeEmail)}>
                {row.designation?.trim() ? row.designation : "—"}
              </span>
            </div>
          </div>
        ),
        getSortValue: (row) => row.employeeName,
        getTitleText: (row) => {
          const designation = row.designation?.trim();
          return designation
            ? `${row.employeeName} — ${designation}`
            : row.employeeName;
        },
      },
      {
        id: "managerName",
        header: "Reporting Manager",
        accessor: "managerName",
        sortable: true,
        truncate: true,
        render: (row) => row.managerName?.trim() || "—",
      },
      {
        id: "employeeCode",
        header: "Employee Code",
        accessor: "employeeCode",
        sortable: true,
        nowrap: true,
        truncate: true,
      },
      {
        id: "employeeType",
        header: "Employee Type",
        accessor: "employeeType",
        sortable: true,
        nowrap: true,
        render: (row) => row.employeeType?.trim() ? employeeTypeLabel(row.employeeType) : "—",
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        render: () => <Badge variant="warning">Pending</Badge>,
        getSortValue: () => "Pending",
      },
      {
        id: "actions",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <Button
            type="button"
            variant="accent"
            size="sm"
            aria-label={`Send invite to ${row.employeeName}`}
            onClick={() => openInviteModal(row)}
          >
            Send Invite
          </Button>
        ),
      },
    ],
    [openInviteModal],
  );

  const visibleColumns =
    filters.status === PENDING_PRE_JOINING_STATUS
      ? pendingPreJoiningColumns
      : columns;

  return (
    <PageShell>
      <ApprovalCenterStats
        stats={stats}
        loading={statsLoading}
        activeStatus={filters.status}
        onSelect={handleSelectStats}
        approvedOnly={isFinanceOnly}
        isSqe={isSqe}
      />

      <ApprovalCenterFilters
        value={filters}
        employeeOptions={employeeOptions}
        employeesLoading={employeesLoading}
        loading={loading}
        hideStatusFilter={isFinanceOnly}
        hideRegionFilter={isSqe}
        statusOptions={isSqe ? SQE_APPROVAL_CENTER_STATUS_OPTIONS : undefined}
        defaultStatus={isSqe ? SQE_DEFAULT_APPROVAL_CENTER_STATUS : undefined}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {loading ? (
        <Spinner />
      ) : (
        <div className={styles.tableSection}>
          <ResponsiveDataTable<ApprovalCenterTableRow>
            columns={visibleColumns}
            data={tableRows}
            defaultSortKey="phase2CreatedOn"
            defaultSortDir="desc"
            aria-label="Bank and document approval requests"
            emptyState={
              <EmptyStateMessage message="No approval requests found." />
            }
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={totalElements}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 30, 40]}
            totalLabel={
              <>
                Total Requests: <strong>{totalElements}</strong>
              </>
            }
          />
        </div>
      )}

      {isFinanceOnly ? (
        <BankDetailsReadOnlyModal
          open={modalOpen}
          onClose={closeModal}
          row={selectedRow}
          fetchChequeBlob={fetchChequeBlob}
        />
      ) : (
        <EmployeeVerificationModal
          open={modalOpen}
          onClose={closeModal}
          row={selectedRow}
          busy={actionBusy}
          onApproveBank={handleApproveBank}
          onRejectBank={handleRejectBank}
          onApproveDocument={handleApproveDocument}
          onRejectDocument={handleRejectDocument}
          onBatchComplete={handleBatchComplete}
          fetchChequeBlob={fetchChequeBlob}
          fetchFileBlob={fetchFileBlob}
        />
      )}

      <InvitePostJoinModal
        open={inviteOpen}
        onClose={closeInviteModal}
        onInvited={() => void refreshAll()}
        initialEmployeeId={inviteEmployeeId ?? undefined}
        lockInitialEmployee
      />
    </PageShell>
  );
}
